import type { CursorData } from '@alpod-ai-writer/shared';
import * as Sentry from '@sentry/react';
import { slateNodesToInsertDelta } from '@slate-yjs/core';
import clsx from 'clsx';
import { Button } from 'flowbite-react';
import { LoaderCircleIcon, UploadIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';

import { ApiError } from '../../api/apiError';
import { useAccessToken } from '../../api/useAccessToken.hook';
import { useApi } from '../../api/useApi.hook';
import { useCredits } from '../../app/hooks/useCredits.hook';
import { AiFeatureType } from '../../app/types/credit.types';
import { ErrorSwitch } from '../../components/errorMessage/errorSwitch.component';
import { ConfirmationModal } from '../../components/modals/confirmation/confirmationModal.component';
import { CreditConfirmationModal } from '../../components/modals/creditConfirmation/creditConfirmationModal.component';
import { InsufficientCreditsModal } from '../../components/modals/insufficientCredits/insufficientCreditsModal.component';
import { Pagination } from '../../components/pagination/pagination.component';
import { useModals } from '../../hooks/useModals.hook';
import { ImportDocumentMaxSize } from '../constants';
import { TextEditor } from './textEditor.component';

const BLOCKS_PER_PAGE = 30;

export interface DocumentImportSectionProps {
    yDoc: Y.Doc;
    documentField: string;
    cursorData: CursorData;
    toolbarEl?: HTMLDivElement|null;
    className?: string;
}

export const DocumentImportSection: React.FC<DocumentImportSectionProps> = ({
    yDoc,
    documentField,
    toolbarEl,
    cursorData,
    className
}) => {
    const api = useApi();
    const accessToken = useAccessToken();
    const { showModal } = useModals();
    const { canUseFeature } = useCredits();

    const [ isLoading, setIsLoading ] = useState(false);
    const [ lastError, setLastError ] = useState<Error|ApiError|string|null>(null);
    const [ isDraggingOver, setIsDraggingOver ] = useState(false);
    const sessionKey = `importSection_${yDoc.guid}_page`;
    const [ currentPage, setCurrentPage ] = useState(() => {
        const saved = sessionStorage.getItem(sessionKey);
        return saved !== null ? parseInt(saved, 10) : 0;
    });
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Get or create the Y.Array for pages
    const yPages = useMemo(() => {
        // We need to define type argument explicitly here otherwise TS compiler crashes with unknown error
        return yDoc.getArray<Y.XmlText>(documentField + '_pages');
    }, [ yDoc, documentField ]);

    const [ totalPages, setTotalPages ] = useState(yPages.length);

    const currentYText = useMemo(() => {
        if (currentPage >= yPages.length || yPages.length === 0) {
            return null;
        }

        return yPages.get(currentPage);
    }, [ yPages, currentPage, totalPages ]);

    useEffect(() => {
        const onChange = () => {
            setTotalPages(yPages.length);
            // Reset to first page if current page no longer exists
            if (currentPage >= yPages.length && yPages.length > 0) {
                setCurrentPage(0);
                sessionStorage.removeItem(sessionKey);
            }
        };

        yPages.observe(onChange);
        onChange();

        return () => yPages.unobserve(onChange);
    }, [ yPages, currentPage, sessionKey ]);

    const handlePageChange = useCallback((pageIndex: number) => {
        setCurrentPage(pageIndex);
        sessionStorage.setItem(sessionKey, String(pageIndex));
    }, [sessionKey]);

    const uploadFile = useCallback((file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        setIsLoading(true);
        setLastError(null);

        fetch('/exporter/documents/import', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            body: formData
        }).then(async (response) => {
            try {
                if (response.status === 200) {
                    const resData = await response.json();

                    if (resData instanceof Object && 'contents' in resData) {
                        console.debug('Document imported successfully:', resData);

                        // Split contents into pages of 30 blocks each
                        const contents = Array.isArray(resData.contents) ? resData.contents : [];

                        // Clear existing pages
                        yDoc.transact(() => {
                            while (yPages.length > 0) {
                                yPages.delete(0);
                            }

                            for (let i = 0; i < contents.length; i += BLOCKS_PER_PAGE) {
                                const pageBlocks = contents.slice(i, i + BLOCKS_PER_PAGE);
                                const pageText = new Y.XmlText();
                                const delta = slateNodesToInsertDelta(pageBlocks);
                                pageText.applyDelta(delta);
                                yPages.push([pageText]);
                                console.debug(`Created page ${Math.floor(i / BLOCKS_PER_PAGE) + 1} with ${pageBlocks.length} blocks`);
                            }

                            console.debug(`Total pages created: ${yPages.length}`);
                        });

                        // Reset to first page
                        setCurrentPage(0);
                        sessionStorage.removeItem(sessionKey);
                    } else {
                        console.error('Invalid response from import API', resData);
                        throw new Error(`Import API returned invalid response: ${JSON.stringify(resData)}`);
                    }
                } else if (response.status === 422) {
                    const responseData = await response.json();
                    throw new InvalidFileError(`Validation error: ${JSON.stringify(responseData)}`);
                } else {
                    const responseText = await response.text();
                    throw new Error(`Import API returned status ${response.status}, response text: ${responseText}`);
                }
            } catch (err) {
                console.error('Failed to import document', err);
                Sentry.captureException(err);

                if (err instanceof Error || err instanceof ApiError) {
                    setLastError(err);
                } else {
                    setLastError(String(err));
                }
            }
        }, (err) => {
            console.error('Failed to import document', err);
            Sentry.captureException(err);

            if (err instanceof Error || err instanceof ApiError) {
                setLastError(err);
            } else {
                setLastError(String(err));
            }
        }).finally(() => {
            setIsLoading(false);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        });
    }, [ accessToken, yDoc, yPages, sessionKey ]);

    const processFile = useCallback((file: File) => {
        if (file.size > ImportDocumentMaxSize) {
            setLastError(new FileTooLargeError(`Nahraný soubor je příliš velký. Maximální povolená velikost je ${Math.floor(ImportDocumentMaxSize / (1024 * 1024))} MB.`));
            return;
        }

        const { canUse, requiredCredits, currentBalance } = canUseFeature(AiFeatureType.DOCUMENT_UPLOAD, 1);

        if (!canUse) {
            showModal('insufficient-credits-import', InsufficientCreditsModal, {
                currentBalance,
                requiredCredits,
                featureName: 'Import dokumentu',
            });
            return;
        }

        showModal('credit-confirm', CreditConfirmationModal, {
            creditCost: requiredCredits,
            featureName: 'Import dokumentu',
            onConfirm: () => uploadFile(file),
        });
    }, [ canUseFeature, showModal, uploadFile ]);

    const handleFileInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            processFile(file);
        }
    }, [ processFile ]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [ processFile ]);

    const handleOverrideClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleRemoveClick = useCallback(() => {
        showModal('confirm', ConfirmationModal, {
            title: 'Potvrdit akci',
            message: 'Opravdu chcete odstranit importovaný dokument?',
            onConfirm: async () => {
                yDoc.transact(() => {
                    while (yPages.length > 0) {
                        yPages.delete(0);
                    }
                });
                setCurrentPage(0);
                sessionStorage.removeItem(sessionKey);
                return true;
            }
        });
    }, [ yDoc, yPages, showModal, sessionKey ]);

    const isContentsEmpty = totalPages === 0;
    const wouldOverride = !isContentsEmpty;

    const textEditor = useMemo(() => {
        if (!currentYText) {
            return null;
        }

        const currentPageField = `${documentField}_page_${currentPage}`;

        return (
            <>
                <TextEditor
                    documentField={currentPageField}
                    documentFieldList={[]}
                    cursorData={cursorData}
                    yjsNode={currentYText}
                    displayWordCount={true}
                    className='relative w-full p-4 lg:p-8 bg-white rounded-md'
                    toolbarEl={toolbarEl}
                    overrideReadOnly={true}
                />
            </>
        );
    }, [ currentYText, documentField, currentPage, totalPages, handlePageChange, cursorData, toolbarEl ]);

    return (
        <div className={clsx(
            'flex flex-col gap-4 md:gap-6',
            className
        )}>
            <div className='flex flex-col gap-4 px-5'>
                <h1 className='text-2xl md:text-3xl font-medium text-[#0b111e] mb-1'>Nahrát práci</h1>
                <p className='text-sm text-gray-700'>Níže můžete nahrát dokument DOCX. Jeho obsah bude importován do editoru níže, ze kterého můžete obsah následně vykopírovat do jednotlivých kapitol dokumentu.</p>
            </div>
            <label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept='application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword, application/vnd.oasis.opendocument.text'
                    className='hidden'
                    onChange={handleFileInput}
                />
                {!wouldOverride && (
                    <div
                        className={clsx(
                            'border rounded-lg p-2 m-4 md:m-0 transition-colors duration-200 group',
                            !isLoading && 'border-dashed cursor-pointer',
                            !isLoading && !isDraggingOver && 'border-gray-400 hover:border-primary-500',
                            !isLoading && isDraggingOver && 'border-primary-500',
                            isLoading && 'border-dashed border-transparent cursor-auto pointer-events-none'
                        )}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <p className={clsx(
                            'flex flex-row gap-2 items-center justify-center text-sm p-4 rounded-md font-semibold transition-colors duration-200',
                            !isLoading && !isDraggingOver && 'bg-gray-200 group-hover:bg-primary-200 text-gray-700 group-hover:text-primary-700',
                            !isLoading && isDraggingOver && 'bg-primary-200 text-primary-700',
                            isLoading && 'bg-gray-200 text-gray-700'
                        )}>
                            {!isLoading && (
                                <><UploadIcon className='flex-shrink-0 w-4 h-4' /> Pro nahrání dokumentu klikněte nebo sem dokument přetáhněte.</>
                            )}
                            {isLoading && (
                                <><LoaderCircleIcon className='flex-shrink-0 w-4 h-4 animate-spin' /> Probíhá nahrávání a zpracování dokumentu...</>
                            )}
                        </p>
                    </div>
                )}
                {wouldOverride && (
                    <div
                        className={clsx(
                            'border rounded-lg p-2 m-4 md:m-0',
                        )}
                    >
                        <div className={clsx(
                            'flex flex-col md:flex-row gap-2 gap-y-4 items-center justify-center text-sm p-4 rounded-md',
                            isLoading && 'bg-gray-200 text-gray-700'
                        )}>
                            {!isLoading && (
                                <>
                                    <p>Níže naleznete již importovaný dokument. Jeho obsah můžete rozkopírovat do jednotlivých kapitol. Také můžete importovat další soubor, ale <span className='font-semibold'>přepíšete tím stávající obsah</span> editoru níže.</p>
                                    <div className='flex flex-col items-end flex-shrink-0 gap-2'>
                                        <Button
                                            color='primary'
                                            onClick={handleOverrideClick}
                                            className='flex-shrink-0'
                                        >Importovat nový dokument</Button>
                                        <button
                                            onClick={handleRemoveClick}
                                            className='flex-shrink-0 underline text-gray-500 hover:text-red-700 hover:no-underline text-xs'
                                        >Odstranit importovaný dokument</button>
                                    </div>
                                </>
                            )}
                            {isLoading && (
                                <><LoaderCircleIcon className='flex-shrink-0 w-4 h-4 animate-spin' /> Probíhá nahrávání a zpracování dokumentu...</>
                            )}
                        </div>
                    </div>
                )}
            </label>

            <ErrorSwitch
                error={lastError}
                default={{ message: 'Nepodařilo se importovat dokument. Zkontrolujte prosím, že nahrávaný dokument je validní DOCX soubor. Pokud problém přetrvává, kontaktujte prosím podporu.' }}
                options={[
                    {
                        errorName: 'InvalidFileError',
                        message: 'Nahraný soubor není platný DOCX dokument.'
                    },
                    {
                        errorName: 'FileTooLargeError',
                        message: `Nahraný soubor je příliš velký. Maximální povolená velikost je ${Math.floor(ImportDocumentMaxSize / (1024 * 1024))} MB.`
                    }
                ]}
                className='grow p-4 bg-red-200 rounded-md'
            />

            {!isContentsEmpty && (
                <>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        className='px-4'
                    />
                    {textEditor}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        className='px-4'
                    />
                </>
            )}
        </div>
    );
};

class InvalidFileError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'InvalidFileError';
    }
}

class FileTooLargeError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'FileTooLargeError';
    }
}
